import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link2,
  Undo,
  Redo,
} from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

export type HeadingLevel = 1 | 2 | 3;

/**
 * Canonical heading levels shared by the rich-text editors (employee notes and
 * the vacancy description editor) so their toolbars stay in lockstep.
 */
export const RICH_TEXT_HEADING_LEVELS: readonly HeadingLevel[] = [1, 2, 3];

const HEADING_ICONS: Record<HeadingLevel, typeof Heading1> = {
  1: Heading1,
  2: Heading2,
  3: Heading3,
};

interface EditorToolbarProps {
  editor: Editor | null;
  /** Heading levels to expose. Omit/empty to hide heading buttons. */
  headingLevels?: readonly HeadingLevel[];
  className?: string;
}

/**
 * Shared formatting toolbar for Tiptap editors. Drives an editor instance owned
 * by the parent so it composes with editors that carry extra extensions (e.g.
 * candidate-note mentions) — used by the vacancy description editor and the
 * candidate note composer/edit dialog. Subscribes to the editor's transactions
 * so the active-state highlighting stays in sync with the selection.
 */
export function EditorToolbar({
  editor,
  headingLevels = [],
  className,
}: EditorToolbarProps) {
  const [, forceRender] = useState({});

  useEffect(() => {
    if (!editor) return;
    const update = () => forceRender({});
    editor.on('transaction', update);
    editor.on('selectionUpdate', update);
    return () => {
      editor.off('transaction', update);
      editor.off('selectionUpdate', update);
    };
  }, [editor]);

  if (!editor) return null;

  const addLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter URL:', previous ?? '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className={cn('flex items-center gap-1 border-b border-border p-2 flex-wrap', className)}>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold (⌘B)"
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic (⌘I)"
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
        title="Underline (⌘U)"
      >
        <UnderlineIcon className="h-4 w-4" />
      </ToolbarButton>

      {headingLevels.length > 0 && (
        <>
          <Divider />
          {headingLevels.map((level) => {
            const Icon = HEADING_ICONS[level];
            return (
              <ToolbarButton
                key={level}
                onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
                active={editor.isActive('heading', { level })}
                title={`Heading ${level}`}
              >
                <Icon className="h-4 w-4" />
              </ToolbarButton>
            );
          })}
        </>
      )}

      <Divider />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton onClick={addLink} active={editor.isActive('link')} title="Insert Link (⌘K)">
        <Link2 className="h-4 w-4" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (⌘Z)"
      >
        <Undo className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (⌘⇧Z)"
      >
        <Redo className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, active, disabled, title, children }: ToolbarButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn('h-8 w-8 p-0', active && 'bg-muted')}
    >
      {children}
    </Button>
  );
}

function Divider() {
  return <div className="h-6 w-px bg-border mx-1" />;
}
